<template>
  <Grid
    gta="'y-axis chart' '. bar-labels'"
    gtc="auto 1fr"
    gtr="1fr var(--r-space-md)"
    gap="8px 0"
    class="custom-chart full-height"
  >
    <Grid :gtc="chartData.yLabels ? 'auto 3px' : '3px'" gap="var(--r-space-xs)" class="y-axis">
      <div v-if="chartData.yLabels" class="full-height relative-position">
        <div v-for="label of yLabels" :key="label.text" class="y-axis__label">
          <span class="text-caption-1" style="visibility: hidden">{{ label.text }}</span>

          <div
            :class="chartData.yLabels.classColor"
            :style="{ bottom: label.bottom, right: 0 }"
            class="text-caption-1 absolute"
          >
            {{ label.text }}
          </div>
        </div>
      </div>
      <VerticalLineArrow :style="{ height: `calc(100% - ${getHeight(Math.abs(minBarValue))}px)` }" />
    </Grid>

    <Grid v-if="height" :gap="chartData.barGap" justify-content="start" align-items="end" gaf="column" class="chart">
      <template v-if="!chartData.group">
        <ChartBar
          v-for="(bar, index) of bars"
          :key="index"
          :show-values-on-hover="showValuesOnHover"
          :bar="bar"
          :get-height="getHeight"
        />
      </template>

      <template v-for="(group, index) of bars" v-else>
        <Grid :key="index" gaf="column" gap="var(--r-space-sm)" align-items="end">
          <ChartBar
            v-for="(bar, index2) of group"
            :key="index2"
            :show-values-on-hover="showValuesOnHover"
            :bar="bar"
            :get-height="getHeight"
          />
        </Grid>
      </template>

      <Grid
        v-if="chartData.dashedLine"
        align-content="start"
        class="dashed-line"
        :style="{ bottom: `${getHeight(chartData.dashedLine.value - minBarValue)}px` }"
      >
        <div class="label text-secondary-1 text-body-1">
          {{ formattedBudget }}
        </div>

        <div class="line"></div>

        <div v-if="chartData.dashedLine.bottomLabel" class="label text-secondary-1 text-body-1">
          {{ chartData.dashedLine.bottomLabel }}
        </div>
      </Grid>

      <template v-if="chartLines">
        <Grid
          v-for="chartLine of chartLines"
          :key="chartLine"
          align-content="start"
          class="chart-line"
          :class="chartData.lines.classColor"
          :style="{ bottom: `${getHeight(chartLine)}px` }"
        >
        </Grid>
      </template>

      <div class="x-axis" :style="{ bottom: `calc(${getHeight(Math.abs(minBarValue))}px - 1px)` }"></div>
    </Grid>

    <Grid
      gaf="column"
      :gap="chartData.barGap"
      justify-content="start"
      class="bar-labels"
      :style="{ bottom: `-${getHeight(minBarValue)}px` }"
    >
      <template v-if="!chartData.group">
        <BarLabel v-for="(bar, index) of bars" :key="`label-${index}`" :bar="bar" />
      </template>

      <template v-for="(group, index) of bars" v-else>
        <BarLabel :key="`label-${index}`" :bar="group[0]" />
      </template>
    </Grid>
  </Grid>
</template>

<script>
import VerticalLineArrow from '@/reusable/svg-icons/VerticalLineArrow'
import ChartBar from './ChartBar'
import BarLabel from './BarLabel'

export default {
  components: {
    VerticalLineArrow,
    ChartBar,
    BarLabel
  },

  props: {
    chartData: {
      required: true
    },

    showValuesOnHover: Boolean
  },

  data: () => ({
    height: null
  }),

  computed: {
    maxBarValue() {
      const maxBarValue = this.chartData.bars.reduce((acc, curr) => {
        const sum = curr.data.reduce((acc, curr) => {
          acc += Math.abs(curr.value)
          return acc
        }, 0)

        if (sum > acc) acc = sum
        return acc
      }, 0)

      return Math.max(maxBarValue, this.chartData?.dashedLine?.value || 0)
    },

    minBarValue() {
      return this.chartData.bars.reduce((acc, curr) => {
        const negativesSum = this.sumNegatives(curr.data)

        if (negativesSum < acc) acc = negativesSum
        return acc
      }, 0)
    },

    bars() {
      const barsWithHeight = this.chartData.bars.map(bar => ({
        ...bar,
        bottom: this.getHeight(this.minBarValue - this.sumNegatives(bar.data))
      }))

      if (!this.chartData.group) return barsWithHeight

      return this.getGroupedBars(barsWithHeight)
    },

    formattedBudget() {
      const formattedBudget = Intl.NumberFormat('en-US', { notation: 'compact', compactDisplay: 'short' }).format(
        this.chartData.dashedLine.value
      )

      return `${formattedBudget} ${this.settings.currency}`
    },

    chartLines() {
      if (!this.chartData.lines) return
      if (!this.maxBarValue) return []

      const linesGap = this.maxBarValue / (this.chartData.lines.count + 1)
      return [linesGap, linesGap * 2, linesGap * 3]
    },

    yLabels() {
      if (!this.chartData.yLabels) return

      const format = val => Intl.NumberFormat('en-US', { notation: 'compact', compactDisplay: 'short' }).format(val)

      return this.chartLines.map(lineGap => ({
        text: format(lineGap),
        bottom: `calc(${this.getHeight(lineGap)}px - 8px)` // 8px is the line-height of text-caption-1 font
      }))
    }
  },

  mounted() {
    this.height = this.$el.getBoundingClientRect().height - 34 // -24 the bottom labels -10 to leave some space
  },

  methods: {
    getHeight(value) {
      return (Math.abs(value) / (this.maxBarValue - this.minBarValue) || 0) * this.height
    },

    sumNegatives(values) {
      // Finds the negative values and sums them up
      return values.reduce((acc, curr) => {
        if (curr.value < 0) acc += curr.value
        return acc
      }, 0)
    },

    getGroupedBars(bars) {
      const arr = []

      for (let i = 0; i < bars.length; i += 2) {
        if (bars[i + 1]) {
          arr.push([bars[i], bars[i + 1]])
        } else {
          arr.push([bars[i]])
        }
      }

      return arr
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-chart {
  --chart-padding-left: 27px;

  .y-axis {
    grid-area: y-axis;
  }

  .chart {
    grid-area: chart;
    position: relative;
    padding-left: var(--chart-padding-left);

    .dashed-line {
      position: absolute;
      width: 100%;
      padding-left: 10px;
      z-index: 1;

      .label {
        position: absolute;
        width: 100%;
        top: -20px;
        text-align: right;

        ~ .label {
          top: unset;
          bottom: -20px;
        }
      }

      .line {
        border-top: 1px dashed var(--r-grey-2);
      }
    }

    .chart-line {
      position: absolute;
      width: 100%;
      height: 1px;
    }

    .x-axis {
      position: absolute;
      height: 1px;
      width: 100%;
      background: var(--r-grey-2);
    }
  }

  .bar-labels {
    grid-area: bar-labels;
    width: 100%;
    height: 16px;
    padding-left: var(--chart-padding-left);
    text-align: center;
  }
}
</style>
